export const cx = (...classes) => classes.filter(Boolean).join(" ");

export const normalizeId = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

export const formatDateFR = (date) => {
  if (!date) return "";

  try {
    return new Date(date).toLocaleDateString("fr-FR");
  } catch {
    return "";
  }
};

export const formatDateTimeFR = (date) => {
  if (!date) return "";

  try {
    return new Date(date).toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
};

export const stripHtml = (html = "") => {
  if (!html) return "";

  const div = document.createElement("div");
  div.innerHTML = html;

  return div.textContent || div.innerText || "";
};

export const getLogoUrl = (logo) => {
  if (!logo) return "";

  const cleanLogo = String(logo).trim();

  if (!cleanLogo) return "";

  if (cleanLogo.startsWith("http://") || cleanLogo.startsWith("https://")) {
    return cleanLogo;
  }

  if (cleanLogo.startsWith("/")) {
    return `${process.env.REACT_APP_API_URL}${cleanLogo}`;
  }

  return `${process.env.REACT_APP_API_URL}/${cleanLogo}`;
};

export const getDateTimestamp = (date) => {
  if (!date) return null;

  const parsed = new Date(date);
  const timestamp = parsed.getTime();

  return Number.isNaN(timestamp) ? null : timestamp;
};

export const isRelanceDue = (association) => {
  if (!association?.date_relance_asso) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const relanceDate = new Date(association.date_relance_asso);
  relanceDate.setHours(0, 0, 0, 0);

  return relanceDate <= today;
};

export const getRelanceBadgeState = (association) => {
  const timestamp = getDateTimestamp(association?.date_relance_asso);

  if (!timestamp) {
    return {
      label: "Aucune date",
      tone: "muted",
    };
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const relanceDate = new Date(timestamp);
  relanceDate.setHours(0, 0, 0, 0);

  const diffDays = Math.ceil(
    (relanceDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
  );

  if (diffDays < 0) {
    return {
      label: `En retard de ${Math.abs(diffDays)} j`,
      tone: "danger",
    };
  }

  if (diffDays === 0) {
    return {
      label: "Aujourd’hui",
      tone: "danger",
    };
  }

  if (diffDays <= 7) {
    return {
      label: `Dans ${diffDays} j`,
      tone: "warning",
    };
  }

  return {
    label: formatDateFR(association.date_relance_asso),
    tone: "safe",
  };
};

export const getMainContact = (association) => {
  const contacts = Array.isArray(association?.prospects)
    ? association.prospects
    : [];

  return contacts[0] || null;
};

export const getContactsCount = (association) => {
  return Array.isArray(association?.prospects) ? association.prospects.length : 0;
};

export const getClientCardLabelColor = (association) => {
  const status = String(association?.statut_asso || "").toLowerCase();

  if (status.includes("client")) return "#facc15";
  if (status.includes("test")) return "#c084fc";
  if (status.includes("devis")) return "#10b981";
  if (status.includes("recontact")) return "#f472b6";
  if (status.includes("mairie")) return "#a855f7";
  if (status.includes("perdu")) return "#ef4444";

  return "#facc15";
};

export const computePositionFromNeighbors = (cards = [], destinationIndex = 0) => {
  const previousCard = cards[destinationIndex - 1];
  const nextCard = cards[destinationIndex + 1];

  const previousPosition = previousCard
    ? Number(previousCard.kanban_position || 0)
    : null;

  const nextPosition = nextCard
    ? Number(nextCard.kanban_position || 0)
    : null;

  if (previousPosition !== null && nextPosition !== null) {
    return (previousPosition + nextPosition) / 2;
  }

  if (previousPosition !== null) {
    return previousPosition + 1000;
  }

  if (nextPosition !== null) {
    return Math.max(nextPosition / 2, 1);
  }

  return 1000;
};

export const reorderCards = (list, startIndex, endIndex) => {
  const result = Array.from(list || []);
  const [removed] = result.splice(startIndex, 1);

  result.splice(endIndex, 0, removed);

  return result;
};

export const moveCardBetweenColumns = ({
  sourceColumn,
  destinationColumn,
  sourceIndex,
  destinationIndex,
}) => {
  const sourceCards = Array.from(sourceColumn.cards || []);
  const destinationCards = Array.from(destinationColumn.cards || []);
  const [removed] = sourceCards.splice(sourceIndex, 1);

  destinationCards.splice(destinationIndex, 0, removed);

  return {
    sourceColumn: {
      ...sourceColumn,
      cards: sourceCards,
      count: sourceCards.length,
    },
    destinationColumn: {
      ...destinationColumn,
      cards: destinationCards,
      count: destinationCards.length,
    },
    movedCard: removed,
    orderedDestinationCards: destinationCards,
  };
};

export const compareByKanbanPosition = (a, b) => {
  const posA = Number(a.kanban_position || 0);
  const posB = Number(b.kanban_position || 0);

  if (posA !== posB) return posA - posB;

  return String(a.nom_asso || "").localeCompare(String(b.nom_asso || ""), "fr");
};

export const sortClientCards = (cards = [], filters = {}) => {
  const sortMode = filters.sort_relance || "position";

  return [...cards].sort((a, b) => {
    if (sortMode === "relance_asc" || sortMode === "relance_desc") {
      const dateA = getDateTimestamp(a.date_relance_asso);
      const dateB = getDateTimestamp(b.date_relance_asso);

      if (dateA === null && dateB === null) {
        return compareByKanbanPosition(a, b);
      }

      if (dateA === null) return 1;
      if (dateB === null) return -1;

      if (dateA !== dateB) {
        return sortMode === "relance_asc" ? dateA - dateB : dateB - dateA;
      }

      return compareByKanbanPosition(a, b);
    }

    return compareByKanbanPosition(a, b);
  });
};

export const filterClientColumns = (columns = [], filters = {}) => {
  const q = String(filters.q || "").trim().toLowerCase();
  const produit = filters.produit_asso || "all";
  const discipline = filters.discipline_asso || "all";
  const favorite = filters.favorite || "all";

  return (columns || []).map((column) => {
    const filteredCards = (column.cards || []).filter((association) => {
      const contacts = Array.isArray(association.prospects)
        ? association.prospects
        : [];

      const contactsText = contacts
        .map((contact) =>
          [
            contact.nom,
            contact.prenom,
            contact.email,
            contact.telephone,
            contact.fonction,
            contact.notes,
          ]
            .filter(Boolean)
            .join(" "),
        )
        .join(" ");

      const haystack = [
        association.nom_asso,
        association.produit_asso,
        association.statut_asso,
        association.discipline_asso,
        association.adresse_asso,
        association.tel_asso,
        association.mail_asso,
        association.siret_asso,
        association.siteweb_asso,
        stripHtml(association.detail_asso || ""),
        contactsText,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      const matchesSearch = q ? haystack.includes(q) : true;

      const matchesProduit =
        produit === "all"
          ? true
          : String(association.produit_asso || "") === String(produit);

      const matchesDiscipline =
        discipline === "all"
          ? true
          : String(association.discipline_asso || "") === String(discipline);

      const matchesFavorite =
        favorite === "true" ? Boolean(association.is_favorite) : true;

      return (
        matchesSearch &&
        matchesProduit &&
        matchesDiscipline &&
        matchesFavorite
      );
    });

    const sortedCards = sortClientCards(filteredCards, filters);

    return {
      ...column,
      cards: sortedCards,
      count: sortedCards.length,
    };
  });
};

export const getUniqueOptionsFromColumns = (columns = [], field) => {
  const values = [];

  (columns || []).forEach((column) => {
    (column.cards || []).forEach((association) => {
      const value = association?.[field];

      if (value && !values.includes(value)) {
        values.push(value);
      }
    });
  });

  return values.sort((a, b) => String(a).localeCompare(String(b), "fr"));
};

export const mergeOptions = (...lists) => {
  const values = [];

  lists.flat().forEach((value) => {
    const cleanValue = String(value || "").trim();

    if (cleanValue && !values.includes(cleanValue)) {
      values.push(cleanValue);
    }
  });

  return values.sort((a, b) => String(a).localeCompare(String(b), "fr"));
};